import { useEffect, useState } from "react";
import { T } from "./exports";
import { Field, IB, SH, Toggle } from "./UIHelper";
import { normalizeListFnConfig } from "./DynamicValueResolver";

export function JSONKeyInput({keyName, value, onKeyRename, onValueChange, onDelete}){
  const [draft, setDraft] = useState(keyName);
  useEffect(()=>{ setDraft(keyName); },[keyName]);
  const commit = () => {
    const trimmed = draft.trim();
    if(trimmed && trimmed !== keyName) onKeyRename(keyName, trimmed);
    else setDraft(keyName);
  };
  return(
    <div style={{display:"flex",gap:2,marginBottom:3,alignItems:"center"}}>
      <input value={draft} onChange={e=>setDraft(e.target.value)} onBlur={commit} onKeyDown={e=>{if(e.key==="Enter")commit();e.stopPropagation();}} style={{flex:1,...IB}}/>
      <span style={{color:T.textMute,fontSize:9}}>:</span>
      <input value={value} onChange={e=>onValueChange(keyName, e.target.value)} style={{flex:2,...IB}}/>
      <button onClick={()=>onDelete(keyName)} style={{background:"none",border:"none",color:T.red,cursor:"pointer",fontSize:10,padding:0}}>✕</button>
    </div>
  );
}

export function ArrayItemEditor({item, index, onUpdate, onDelete}){
  const isObj = item !== null && typeof item === "object";
  const ObjKeyInput = ({k, v, onChangeVal, onRenameKey, onDelKey}) => {
    const [kDraft, setKDraft] = useState(k);
    useEffect(()=>setKDraft(k),[k]);
    const commitKey = () => { const t=kDraft.trim(); if(t&&t!==k) onRenameKey(k,t); else setKDraft(k); };
    return(
      <div style={{display:"flex",gap:2,marginBottom:2,alignItems:"center"}}>
        <input value={kDraft} onChange={e=>setKDraft(e.target.value)} onBlur={commitKey} onKeyDown={e=>{if(e.key==="Enter")commitKey();e.stopPropagation();}} style={{flex:1,...IB}}/>
        <input value={v??""} onChange={e=>onChangeVal?.(k,e.target.value)} style={{flex:2,...IB}}/>
        <button onClick={()=>onDelKey(k)} style={{background:"none",border:"none",color:T.red,cursor:"pointer",fontSize:10,padding:0}}>✕</button>
      </div>
    );
  };
  if(isObj){
    const keys = Object.keys(item);
    const addKey = () => { const k = `key${keys.length+1}`; onUpdate({...item,[k]:""}); };
    const renameKey = (oldK,newK) => { const n={}; for(const k of Object.keys(item)){ if(k===oldK) n[newK]=item[oldK]; else n[k]=item[k]; } onUpdate(n); };
    const changeVal = (k,v) => onUpdate({...item,[k]:v});
    const delKey = (k) => {const n={...item};delete n[k];onUpdate(n);};
    return(
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:3,padding:5,marginBottom:4}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
          <span style={{fontSize:8,color:T.textSub}}>Item {index+1}</span>
          <div style={{display:"flex",gap:4}}>
            <button onClick={addKey} style={{background:"none",border:"none",color:T.green,cursor:"pointer",fontSize:9,padding:0}}>+ key</button>
            <button onClick={onDelete} style={{background:"none",border:"none",color:T.red,cursor:"pointer",fontSize:9,padding:0}}>✕</button>
          </div>
        </div>
        {keys.map(k=>(<ObjKeyInput key={k} k={k} v={item[k]} onChangeVal={changeVal} onRenameKey={renameKey} onDelKey={delKey}/>))}
      </div>
    );
  }
  return(
    <div style={{display:"flex",gap:2,marginBottom:3,alignItems:"center"}}>
      <input value={item??""} onChange={e=>onUpdate(e.target.value)} style={{flex:1,...IB}}/>
      <button onClick={onDelete} style={{background:"none",border:"none",color:T.red,cursor:"pointer",fontSize:10,padding:0}}>✕</button>
    </div>
  );
}

export function ListFunctionsPanel({ config, onChange, keyOptions = [], allInputVars = [] }) {
  const cfg = normalizeListFnConfig(config);
  const keys = keyOptions || [];
  const vars = allInputVars || [];
  const update = (k, v) => onChange({ ...cfg, [k]: v });

  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 4, padding: 7, marginBottom: 6 }}>
      <div style={{ fontSize: 8, color: T.yellow, marginBottom: 4, fontWeight: 600 }}>List Functions</div>
      <Toggle label="Enable list functions" value={cfg.enabled} onChange={v => update("enabled", v)} />
      {cfg.enabled && <>
        <SH ch="Sort" color={T.blue}/>
        <Toggle label="Enable sort" value={cfg.sortEnabled} onChange={v => update("sortEnabled", v)} />
        {cfg.sortEnabled && <>
          <Field
            label="Sort Key"
            value={cfg.sortKey || ""}
            onChange={v => update("sortKey", v)}
            opts={keys.length ? [{ value: "", label: "- select key -" }, ...keys.map(k => ({ value: k, label: k }))] : undefined}
            placeholder={keys.length ? undefined : "e.g. price or user.name"}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
            <Field label="Direction" value={cfg.sortDirection || "asc"} onChange={v => update("sortDirection", v)} opts={["asc", "desc"]} />
            <Field label="Type" value={cfg.sortType || "auto"} onChange={v => update("sortType", v)} opts={["auto", "string", "number", "date"]} />
          </div>
        </>}

        <SH ch="Filter" color={T.green}/>
        <Toggle label="Enable filter" value={cfg.filterEnabled} onChange={v => update("filterEnabled", v)} />
        {cfg.filterEnabled && <>
          <Field
            label="Filter Key"
            value={cfg.filterKey || ""}
            onChange={v => update("filterKey", v)}
            opts={keys.length ? [{ value: "", label: "- select key -" }, ...keys.map(k => ({ value: k, label: k }))] : undefined}
            placeholder={keys.length ? undefined : "e.g. status or category"}
          />
          <Field label="Condition" value={cfg.filterMode || "contains"} onChange={v => update("filterMode", v)} opts={["contains", "equals", "startsWith", "endsWith", "gt", "gte", "lt", "lte"]} />
          <Field label="Value Source" value={cfg.filterValueSource || "static"} onChange={v => update("filterValueSource", v)} opts={["static", "variable"]} />
          {cfg.filterValueSource === "variable"
            ? <Field
                label="Variable"
                value={cfg.filterVarName || ""}
                onChange={v => update("filterVarName", v)}
                opts={vars.length ? [{ value: "", label: "- select variable -" }, ...vars.map(v => ({ value: v.varName, label: `$${v.varName}` }))] : undefined}
                placeholder={vars.length ? undefined : "e.g. selectedCategory"}
              />
            : <Field label="Static Value" value={cfg.filterValue || ""} onChange={v => update("filterValue", v)} placeholder="e.g. london" />}
          <Toggle label="Case sensitive" value={cfg.filterCaseSensitive || false} onChange={v => update("filterCaseSensitive", v)} />
        </>}

        <SH ch="Custom" color={T.yellow}/>
        <Toggle label="Enable custom transform" value={cfg.customEnabled} onChange={v => update("customEnabled", v)} />
        {cfg.customEnabled && <>
          <Field
            label="Custom JS (return array)"
            rows={4}
            value={cfg.customTransform || ""}
            onChange={v => update("customTransform", v)}
            placeholder={'Example:\nreturn items.filter(x => x.active).slice(0, 10);'}
          />
          <div style={{ fontSize: 8, color: T.textMute, lineHeight: 1.5 }}>
            Runtime variables are available as <span style={{ color: T.blue, fontFamily: "monospace" }}>vars</span>.
          </div>
        </>}
      </>}
    </div>
  );
}
